import { useGetPresentersList } from '@/hooks/presenter';
import { useAllVoices } from '@/hooks/useAllVoices';
import usePageTitle from '@/hooks/usePageTitle';
import { useAutosaveDraftAgent, useEditAgentInit, useEditAgentSync, useLoadAgentIntoStore } from '@/pages/agents/hooks';
import { useAnalytics } from '@/services/analytics';
import { getKnowledge } from '@/services/api';
import { isAgentInProgress } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import { AgentCreateTabType, AgentStatus, IAgent, PresenterUsecase } from '@d-id/types';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useGetAgent } from '../../api/useGetAgent';
import { AgentEditor } from './AgentEditor';

const LoadingPage = styled.div`
    background-color: ${theme.colors.black[200]};
    width: 100%;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
`;

interface Props {
    agent: IAgent;
    initialTab?: AgentCreateTabType;
    isEditAgent?: boolean;
}

const AgentEditorPage = observer(({ agent: initialAgent, initialTab, isEditAgent = false }: Props) => {
    const agentEditorStore = useAgentEditorStore();
    const mixpanel = useAnalytics();
    const [agent, setAgent] = useState<IAgent>(initialAgent);

    usePageTitle(isEditAgent ? 'Edit agent' : 'New agent');

    useEditAgentSync();

    useEffect(() => {
        mixpanel?.track('agent-editor', { event: 'loaded', page: agentEditorStore.currentTab });
    }, [agentEditorStore.currentTab]);

    // todo: remove BEN YAEL after migration DBs
    useEffect(() => {
        const fetchKnowledgeDeprecated = async () => {
            if (!!agent?.knowledge?.id) {
                const getKnowledgeReq = await getKnowledge(agent.knowledge.id);
                // @ts-ignore
                setAgent(prev => ({ ...prev, knowledge: { id: agent.knowledge!.id!, provider: 'pinecone', ...getKnowledgeReq } }));
            }
        };
        fetchKnowledgeDeprecated();
    }, [agent?.knowledge?.id]);

    useEffect(() => {
        if (initialTab) {
            agentEditorStore.setCurrentTab(initialTab);
        }
    }, []);

    useEffect(() => {
        agentEditorStore.setIsAgentEdit(isEditAgent);
    }, [isEditAgent]);

    if (isEditAgent && agent) {
        useEditAgentInit(agent);
    } else {
        useLoadAgentIntoStore({ callback: setAgent, agent });
        useAutosaveDraftAgent();
    }

    if (agentEditorStore.isEditorLoading) {
        return (
            <LoadingPage>
                <CircularProgress size={96} />
            </LoadingPage>
        );
    }

    return <AgentEditor agent={agent} />;
});

const AgentEditorLoadWrapper = observer((props: Omit<Props, 'agent'>) => {
    const agentEditorStore = useAgentEditorStore();

    const { isLoading: isPresentersLoading, data: presenters } = useGetPresentersList({ usecase: PresenterUsecase.Agent });
    const { isLoading: isVoicesLoading, data: voices } = useAllVoices();
    const { agentId } = useParams<{ agentId: string }>();

    const [forceRefetch] = useState(new Date().getTime());
    const { data, isLoading: isAgentLoading } = useGetAgent(agentId!, true, forceRefetch);

    useEffect(() => {
        if (data) {
            agentEditorStore.setInputAgent(data);
        }
    }, [data, agentEditorStore]);

    if (
        isPresentersLoading ||
        isVoicesLoading ||
        isAgentLoading ||
        !presenters?.length ||
        !voices?.length ||
        !Object.keys(agentEditorStore.inputAgent).length
    ) {
        return (
            <LoadingPage>
                <CircularProgress size={96} />
            </LoadingPage>
        );
    }

    if (!data || isAgentInProgress(data.status)) {
        return <Navigate to={AppRoutes.Agent} replace />;
    }

    return <AgentEditorPage {...props} agent={data} isEditAgent={data.status === AgentStatus.Done} />;
});

export { AgentEditorLoadWrapper as AgentEditorPage };
